import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { SessionManagerService } from '../../services/session-manager.service';
import { SessionStore } from '../../stores/session.store';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-session-notifications',
  standalone: true,
  imports: [CommonModule],
  // No <p-toast> here: messages go to the root MessageService and are shown by
  // the one in AppComponent. A second keyless toast host rendered every global
  // toast twice (MYB-5193).
  template: ``,
  styles: []
})
export class SessionNotificationsComponent implements OnInit, OnDestroy {
  private sessionManager = inject(SessionManagerService);
  private sessionStore = inject(SessionStore);
  private messageService = inject(MessageService);
  private destroy$ = new Subject<void>();

  private lastRefreshState = false;
  private lastErrorState: string | null = null;

  ngOnInit(): void {
    // Use effect() in constructor with proper injection context
    this.setupStateMonitoring();
  }

  private setupStateMonitoring(): void {
    // Use a polling approach with timer to check for state changes
    const checkInterval = setInterval(() => {
      this.checkRefreshState();
      this.checkErrorState();
    }, 500); // Check every 500ms

    // Clean up interval on destroy
    this.destroy$.subscribe(() => {
      clearInterval(checkInterval);
    });
  }

  private checkRefreshState(): void {
    const isRefreshing = this.sessionManager.isRefreshing();
    const wasRefreshing = this.lastRefreshState;

    if (wasRefreshing && !isRefreshing) {
      // Just finished refreshing
      if (this.sessionManager.isAuthenticated()) {
        this.showTokenRefreshedNotification();
      } else {
        this.showRefreshErrorNotification();
      }
    }

    this.lastRefreshState = isRefreshing;
  }

  private checkErrorState(): void {
    const errorMessage = this.sessionManager.errorMessage();
    const lastError = this.lastErrorState;

    if (errorMessage && errorMessage !== lastError) {
      this.showRefreshErrorNotification();
    }

    this.lastErrorState = errorMessage;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showTokenRefreshedNotification(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Session Extended',
      detail: 'Your session has been automatically extended.',
      life: 3000,
      key: 'session-notification'
    });
  }

  private showRefreshErrorNotification(): void {
    const failureCount = this.sessionStore.refreshFailureCount();
    let detail = 'There was an issue extending your session.';

    if (failureCount > 0) {
      detail = `Failed to extend session (attempt ${failureCount}/3). Please save your work.`;
    }

    if (failureCount >= 2) {
      detail = 'Session extension failed multiple times. Your session will expire soon.';
    }

    this.messageService.add({
      severity: 'warn',
      summary: 'Session Warning',
      detail: detail,
      life: 5000,
      key: 'session-notification'
    });
  }
}